import { defineStore } from 'pinia'
import { User, Permission, UserEvent } from '@letsdial/api/src/lib/types/User'
import useLetsDialAPI from '../services/letsDialAPI/useLetsDialAPI'
import { Role } from '@letsdial/api/src/lib/types/Roles'
import { DateTime } from 'luxon'
import _ from 'lodash'
import { AxiosError } from 'axios'
import router from '../router'
import useLetsDialSocket from '../services/letsDialSocket/useLetsDialSocket'
import useLetsDialPhone from '../services/letsDialPhone/useLetsDialPhone'
import { Organization } from '@letsdial/api/src/lib/types/Organization'
import * as Sentry from '@sentry/vue'
import { PauseCode } from '@letsdial/api/src'
import useGetStream from '../services/letsDialSocket/useGetStream'
import { useInitStore } from './initStore'
import { useNotificationStore } from '/@/store/notificationStore'
import { usePhoneStore } from './phoneStore'
import { useFilterStore } from './filterStore'

declare let localStorage: any

let socketClose: any
let availabilityRequestSequence = 0

// useStore could be anything like useUser, useCart
export const useAuthStore = defineStore({
  // unique id of the store across your application
  id: 'auth',
  state: () => {
    const org = localStorage.getItem('selected:organization')
    const storedUser = localStorage.getItem('auth:user')
    const user = storedUser ? (JSON.parse(storedUser) as User) : undefined
    return {
      extensionPassword: undefined,
      user,
      currentStatus: user?.attributes.status ?? 'UNKNOWN',
      pause: undefined,
      statusChangeAt: user?.attributes.statusChangedAt
        ? DateTime.fromISO(user.attributes.statusChangedAt)
        : undefined,
      roles: [],
      organizations: [],
      selectedOrganization: org ? (JSON.parse(org) as Organization) : undefined,
      features: [],
      refreshDash: false,
      microphonePermission: false,
    } as {
      extensionPassword?: string
      user?: User
      roles: Array<Role>
      currentStatus: string
      pause?: PauseCode
      statusChangeAt?: DateTime
      organizations: Array<Organization>
      selectedOrganization?: Organization
      features: Array<string>
      refreshDash: boolean
      microphonePermission: boolean
    }
  },
  getters: {
    isAuthenticated(): boolean {
      return this.extensionPassword ? true : false
    },
    permissions(): Array<Permission> {
      return _.uniqWith(
        [
          ...(this.user?.attributes?.permissions ?? []),
          ...(this.selectedOrganization?.meta?.permissions ?? []),
          ...this.roles.reduce((permissions, role) => {
            permissions = [
              ...permissions,
              ...(role.attributes?.permissions ?? []),
            ]
            return permissions
          }, [] as Array<Permission>),
        ],
        _.isEqual,
      )
    },
    //Features
    hasFeatureVoicemailDetection(): boolean {
      return this.features.includes('VOICEMAIL_DETECTION')
    },
    hasFeatureB12Dialler(): boolean {
      return this.features.includes('B12_DIALLER')
    },
    hasFeatureCallbi(): boolean {
      return this.features.includes('CALLBI')
    },
    hasFeatureAVM(): boolean {
      return this.features.includes('AVM')
    },
  },
  actions: {
    async load() {
      console.log('AuthStore.Load')
      if (localStorage.getItem('extensionPassword')) {
        console.log('Getting Extension Password')
        this.setExtensionPassword(
          localStorage.getItem('extensionPassword') ?? undefined,
        )
        await this.getAuthenticatedUser(true)
      }
      if (this.selectedOrganization) {
        Sentry.setTags({
          organization_name: this.selectedOrganization.attributes.name,
          organization_id: this.selectedOrganization.id,
        })
      }
    },
    can(action: string, subject: string) {
      return (
        this.permissions.filter(
          (perm) =>
            perm.action === action &&
            (perm.subject === subject || perm.subject === 'all'),
        ).length > 0
      )
    },
    async getAuthenticatedUser(firstLoad = false) {
      console.log('AuthStore.getAuthenticatedUser')
      if (this.user && !firstLoad) {
        Sentry.setUser({
          id: `${this.user.id}`,
          username: this.user.attributes.username,
          email: this.user.attributes.email,
        })
        return this.user
      } else {
        const api = useLetsDialAPI()
        try {
          const userRequest = await api.users.get('me', {
            include: 'organizations,roles',
          })
          if (userRequest.data) {
            const user = userRequest.data
            this.user = user
            this.currentStatus = user.attributes.status ?? 'UNKNOWN'
            this.statusChangeAt = user.attributes.statusChangedAt
              ? DateTime.fromISO(user.attributes.statusChangedAt)
              : undefined
            localStorage.setItem('auth:user', JSON.stringify(user))
            Sentry.setUser({
              id: `${user.id}`,
              username: user.attributes.username,
              email: user.attributes.email,
            })
            if (userRequest.data.attributes.isStaff) {
              const { data: organizations } = await api.organizations.list()
              this.organizations = organizations ?? []
            } else {
              this.organizations =
                (userRequest.included?.filter(
                  (inc: any) => inc.type === 'organizations',
                ) as Array<Organization>) ?? []
            }
            if (this.organizations.length === 1 && !this.selectedOrganization) {
              this.selectedOrganization = this.organizations[0]
              if (user.id) {
                await api.users.patch(user.id, {
                  type: 'users',
                  id: user.id,
                  attributes: {
                    currentOrganizationId: this.selectedOrganization.id,
                  },
                })
              }
              localStorage.setItem(
                'selected:organization',
                JSON.stringify(this.selectedOrganization),
              )
              Sentry.setTags({
                organization_name: this.selectedOrganization.attributes.name,
                organization_id: this.selectedOrganization.id,
              })
            }
            this.roles =
              (userRequest.included?.filter(
                (inc: any) => inc.type === 'roles',
              ) as Array<Role>) ?? []
            const features = await api.features.list()
            this.features = features
          }
        } catch {
          this.logout()
        }
      }
      return this.user
    },
    async mount() {
      console.log('AUTH STORE MOUNT')
      await this.getAuthenticatedUser()
    },
    unmount() {
      if (socketClose) {
        socketClose()
      }
    },
    loadSocket() {
      const { leave } = useGetStream<UserEvent>(
        'users:stream',
        { filters: { userId: 'me', types: 'users' } },
        (data) => {
          for (const [key, value] of Object.entries(data.data)) {
            this.setAttribute(key, value)
          }
        },
      )
      socketClose = leave
    },
    async setSelectedOrganization(org?: Organization) {
      if (this.user?.id && org) {
        this.refreshDash = true
        const api = useLetsDialAPI()
        this.user.attributes.currentOrganizationId = org.id
        this.user.attributes.currentOrganizationId = org.id
        await api.users.patch(this.user.id, {
          type: 'users',
          id: this.user.id,
          attributes: {
            currentOrganizationId: org.id,
          },
        })
      }
      this.selectedOrganization = org
      localStorage.setItem('selected:organization', JSON.stringify(org))
      const filterStore = useFilterStore()
      filterStore.clearAll()
      router.push({ name: 'Home' })
      this.refreshDash = false
    },
    updateSelectedOrganizationCredits(credits: number) {
      if (this.selectedOrganization?.attributes?.creditsInCents !== undefined) {
        this.selectedOrganization.attributes.creditsInCents = credits
      }
    },
    async logout() {
      const api = useLetsDialAPI()
      const { socket } = useLetsDialSocket()
      const { phone } = useLetsDialPhone()
      const initStore = useInitStore()
      // Sentry.configureScope((scope) => scope.setUser(null))
      // this.setExtensionPassword(undefined)
      try {
        if (socketClose) {
          socketClose()
          socketClose = undefined
        }
        socket.close()
        await phone.close()
        initStore.$reset()
        await api.users.logout()
        Sentry.setUser(null)
        Sentry.setTags({
          organization_name: null,
          organization_id: null,
        })
        localStorage.removeItem('extensionPassword')
        localStorage.removeItem('selected:organization')
        localStorage.removeItem('auth:user')
        const filterStore = useFilterStore()
        filterStore.clearAll()
        this.$reset()
        router.push({ name: 'Login' })
      } catch (error: any) {
        const err = error as AxiosError
        if (err.response && err.response.status === 401) {
          localStorage.removeItem('extensionPassword')
          localStorage.removeItem('selected:organization')
          localStorage.removeItem('auth:user')
          const filterStore = useFilterStore()
          filterStore.clearAll()
          this.$reset()
          router.push({ name: 'Login' })
        } else {
          console.error(err)
        }
      }
    },
    setExtensionPassword(extensionPassword?: string) {
      if (extensionPassword) {
        localStorage.setItem('extensionPassword', extensionPassword)
      } else {
        localStorage.removeItem('extensionPassword')
      }
      this.extensionPassword = extensionPassword
    },
    setAttribute(attribute: string, value: string) {
      if (this.user && (this.user.attributes as any)[attribute]) {
        ;(this.user.attributes as any)[attribute] = value
      }
    },
    setPause(pause?: PauseCode) {
      this.pause = pause
    },
    async setAvailabilityStatus(status: string, clearPause = false) {
      availabilityRequestSequence++
      const currentSequence = availabilityRequestSequence

      const notificationStore = useNotificationStore()
      console.log(
        'SET_AVAILABLE_STATUS',
        status,
        this.pause,
        this.pause?.attributes.isAdminPause,
      )
      const { socket } = useLetsDialSocket()
      console.log(status === 'IDLE', this.pause)
      if (this.currentStatus === 'PAUSED' && !clearPause) {
        return
      }
      const requestedStatus =
        status === 'IDLE' && this.pause?.attributes.isAdminPause === true
          ? 'PAUSED'
          : status

      if (requestedStatus === 'IDLE' && !this.microphonePermission) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          })
          stream.getTracks().forEach((track) => track.stop())
          this.microphonePermission = true
        } catch {
          if (availabilityRequestSequence !== currentSequence) {
            return
          }

          notificationStore.setNotification(
            'Error',
            'Microphone not available, phone has been disconnected',
            'Warning',
            undefined,
            5000,
          )
          console.error('Microphone not available, disconnecting phone')

          const phoneStore = usePhoneStore()
          phoneStore.setStatus('DISCONNECTED')
          socket.emit('user:set:phone:status', { status: 'DISCONNECTED' })
          const micStatus = 'MIC_FAILURE'
          await this.setPersistendStatus(micStatus)
          if (availabilityRequestSequence === currentSequence) {
            this.setStatus(micStatus)
          }
          return
        }
      }

      if (availabilityRequestSequence !== currentSequence) {
        console.log('Availability request superseded, aborting status emit')
        return
      }

      try {

        const persistedStatus = await this.setPersistendStatus(requestedStatus)

        if (availabilityRequestSequence === currentSequence) {
          this.setStatus(persistedStatus)
        }
      } catch (error) {
        if (availabilityRequestSequence !== currentSequence) {
          return
        }

        console.error('Unable to change availability status', error)
        notificationStore.setNotification(
          'Error',
          'Unable to change agent status. Please try again.',
          'Warning',
          undefined,
          5000,
        )
      }
    },
    setStatus(status: string) {
      console.log(`Set Status ${status}`)
      this.currentStatus = status
      this.statusChangeAt = DateTime.now()
    },
    async setPersistendStatus(requestedStatus: string) {
      const { socket } = useLetsDialSocket()
      return new Promise<string>((resolve, reject) => {
          const timeout = window.setTimeout(
            () => reject(new Error('Status change was not acknowledged')),
            5000,
          )
          socket.emit(
            'user:set:status',
            {
              status: requestedStatus,
              pauseCodeId: this.pause?.id,
            },
            (result: { ok: boolean; status?: string; error?: string }) => {
              window.clearTimeout(timeout)
              if (result.ok && result.status) {
                resolve(result.status)
              } else {
                reject(new Error(result.error ?? 'Status change failed'))
              }
            },
          )
        })
    },
  },
})
